import i18n from '@/lib/i18n';
import { ApiError } from '@/lib/api-client';

/**
 * The shape a backend error code is allowed to have before it is used as an
 * i18next key.
 *
 * i18next resolves a key by walking the catalog with plain property access, so
 * it reaches `Object.prototype` too: a refusal carrying `code: "constructor"`
 * resolves to `function Object() { [native code] }`, and `code: "toString"` to
 * the function source — and `defaultValue` does not save it, because as far as
 * i18next is concerned the key WAS found. `code` is read verbatim out of the
 * problem-details body, so its value is not ours to trust.
 *
 * Every prototype member is camelCase or underscore-prefixed, so requiring an
 * uppercase first character is what closes the hole; the rest of the pattern
 * just describes the codes we actually issue. The hyphenated tail is optional
 * because `INTERNAL` (`packages/shared/src/errors.ts`) is a real single-word
 * code, alongside `DOMAIN-NNN`, `BAD-GATEWAY` and `AUTH-PW-WEAK`.
 * `errors.test.ts` holds the catalog to it.
 */
const ERROR_CODE = /^[A-Z][A-Z0-9]*(?:-[A-Z0-9]+)*$/;

/**
 * Codes that may be handed to `i18n.t()`. A code of any other shape is treated
 * as if the refusal carried none at all.
 */
export function isErrorCode(code: string | undefined): code is string {
  return typeof code === 'string' && ERROR_CODE.test(code);
}

/**
 * Single source of truth for turning a thrown error into a toast string.
 * Maps the backend error `code` to a localized `errors.<code>` message, falling
 * back to the (JSON-guarded) `displayMessage`. Replaces the per-page `errMsg`
 * helpers that rendered raw problem-details `detail` verbatim.
 */
export function errorMessage(err: unknown, fallback = 'Request failed'): string {
  if (err instanceof ApiError) {
    if (isErrorCode(err.code)) {
      return i18n.t('errors.' + err.code, { defaultValue: err.displayMessage });
    }
    return err.displayMessage;
  }
  if (err instanceof Error) return err.message;
  return fallback;
}

/**
 * The HTTP status behind a thrown request error, when it carries one.
 *
 * Lets a screen separate "you may not read this" from "this is broken" — two
 * refusals that look identical to a `catch` but call for opposite words on
 * screen. Duck-typed rather than `instanceof ApiError`, so a status survives an
 * error that crossed a module boundary (a re-thrown copy, a test double).
 * `undefined` means no status was reported; it must not be read as a 500.
 */
export function errorStatus(err: unknown): number | undefined {
  const status = (err as { status?: unknown } | null | undefined)?.status;
  return typeof status === 'number' ? status : undefined;
}
